import type { ConsoleAlarm } from '../../lib/api'
import { cn } from '../../lib/utils'
import { btn, card, h2Class, kicker, when } from '../soc/SocShell'
import { SectionMessage } from './ConsoleShell'

// BRIEF-F1b, Alarms: platform alarms of the last 14 days
// (/hub/console/alarms), newest first, each with Acknowledge until a staff
// member has. Acknowledging records who and when; nothing else changes.

function AlarmItem({
  alarm,
  onAcknowledge,
  pending,
  ackError,
}: {
  alarm: ConsoleAlarm
  onAcknowledge: (id: string) => void
  pending: boolean
  ackError: string | null
}) {
  const acked = !!alarm.acknowledged_at
  return (
    <li
      data-testid={`alarm-${alarm.id}`}
      className={cn(card, 'flex flex-wrap items-center justify-between gap-4 px-5 py-3.5', !acked && 'border-l-[6px] border-l-brass')}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[16px] font-bold">{alarm.headline ?? alarm.kind}</span>
        <span className="text-[13px] text-[#6b5a3e]">
          {alarm.kind}
          {alarm.subject && ` · ${alarm.subject}`}
          {alarm.day && ` · ${alarm.day}`}
          {alarm.count && alarm.count > 1 ? ` · seen ${alarm.count} times` : ''}
          {alarm.last_seen && ` · last ${when(alarm.last_seen)}`}
          {alarm.outcome && ` · delivery ${alarm.outcome}`}
        </span>
        {ackError && (
          <span role="alert" className="text-[13px] text-danger">
            Could not acknowledge: {ackError}
          </span>
        )}
      </div>
      {acked ? (
        <span className="text-[14px] text-[#6b5a3e]">Acknowledged {when(alarm.acknowledged_at)}</span>
      ) : (
        <button
          type="button"
          className={btn}
          disabled={pending}
          onClick={() => onAcknowledge(alarm.id)}
          aria-label={`Acknowledge ${alarm.headline ?? alarm.kind}`}
        >
          {pending ? 'Acknowledging…' : 'Acknowledge'}
        </button>
      )}
    </li>
  )
}

export function ConsoleAlarms({
  alarms,
  error,
  onAcknowledge,
  pendingIds,
  ackErrors,
}: {
  alarms: ConsoleAlarm[] | null
  error: string | null
  onAcknowledge: (id: string) => void
  pendingIds: ReadonlySet<string>
  ackErrors: Readonly<Record<string, string>>
}) {
  return (
    <section id="alarms" aria-label="Alarms" className="flex flex-col gap-3.5">
      <div>
        <div className={kicker}>Platform alarms, last 14 days</div>
        <h2 className={h2Class}>Alarms</h2>
      </div>
      {error !== null ? (
        <SectionMessage tone="error">Could not load: {error}</SectionMessage>
      ) : alarms === null ? (
        <SectionMessage>Loading the alarms…</SectionMessage>
      ) : alarms.length === 0 ? (
        <SectionMessage>No alarm in the last 14 days.</SectionMessage>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
          {alarms.map((a) => (
            <AlarmItem
              key={a.id}
              alarm={a}
              onAcknowledge={onAcknowledge}
              pending={pendingIds.has(a.id)}
              ackError={ackErrors[a.id] ?? null}
            />
          ))}
        </ul>
      )}
    </section>
  )
}
